import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ClientLogin from './components/ClientLogin';
import ClientDashboard from './components/ClientDashboard';
import { getAuth, setAuth, clearAuth } from './utils/auth';
import { API_URL } from './config';

// ─── Unified Login wrapper with Admin / Client Portal tab switcher ────────────
function UnifiedLogin({ defaultTab, onLogin }) {
  const [tab, setTab] = useState(defaultTab || 'admin');

  const handleAdminLogin = (token, role, name) => {
    onLogin({ type: 'admin', token, role, name });
  };

  const handleClientLogin = (token, data) => {
    onLogin({ type: 'client', token, ...data });
  };

  return (
    <div className="relative">
      {tab === 'admin' ? (
        <Login onLogin={handleAdminLogin} />
      ) : (
        <ClientLogin onLogin={handleClientLogin} />
      )}

      {/* Tab switcher pill — fixed at bottom center */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-[#09090B]/95 backdrop-blur-xl border border-white/10 rounded-full p-1 shadow-2xl shadow-black/50">
        <button
          onClick={() => {
            setTab('admin');
            window.history.replaceState({}, '', '/');
          }}
          className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
            tab === 'admin'
              ? 'bg-[#6366f1] text-black shadow-md'
              : 'text-zinc-500 hover:text-white'
          }`}
        >
          Admin
        </button>
        <button
          onClick={() => {
            setTab('client');
            window.history.replaceState({}, '', '/client');
          }}
          className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
            tab === 'client'
              ? 'bg-[#6366f1] text-white shadow-md'
              : 'text-zinc-500 hover:text-white'
          }`}
        >
          Client Portal
        </button>
      </div>
    </div>
  );
}

// ─── Loading screen shown while restoring session ─────────────────────────────
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-[#6366f1] border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-600 text-xs font-mono tracking-wide">Restoring session…</p>
      </div>
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
function App() {
  // null = still loading; false = unauthenticated; object = authenticated session
  const [authState, setAuthState] = useState(null);
  const [shareToken, setShareToken] = useState(null);

  useEffect(() => {
    // Check for share token in URL (?token=xxx → direct client dashboard access)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setShareToken(urlToken);
      setAuthState(false);
      return;
    }

    // Try to restore session from localStorage
    const stored = getAuth();
    if (stored?.token) {
      fetch(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${stored.token}` },
      })
        .then((res) => {
          if (res.ok) {
            setAuthState(stored);
          } else {
            clearAuth();
            setAuthState(false);
          }
        })
        .catch(() => {
          // Backend unreachable — still trust the cached session
          setAuthState(stored);
        });
    } else {
      setAuthState(false);
    }
  }, []);

  const handleLogin = (data) => {
    setAuth(data);
    setAuthState(data);
  };

  const handleLogout = () => {
    clearAuth();
    setAuthState(false);
    setShareToken(null);
    window.history.pushState({}, '', '/');
  };

  // ── Loading state ──
  if (authState === null) {
    return <LoadingScreen />;
  }

  // ── Share-token mode: direct link, no login required ──
  if (shareToken) {
    return (
      <div className="App">
        <ClientDashboard token={shareToken} />
      </div>
    );
  }

  // ── Authenticated ──
  if (authState) {
    if (authState.type === 'admin') {
      return (
        <div className="App">
          <Toaster position="top-right" theme="dark" richColors closeButton />
          <Dashboard onLogout={handleLogout} />
        </div>
      );
    }
    if (authState.type === 'client') {
      return (
        <div className="App">
          <ClientDashboard
            clientId={authState.clientId}
            onLogout={handleLogout}
          />
        </div>
      );
    }
  }

  // ── Not authenticated: show unified login ──
  const isClientPath = window.location.pathname.startsWith('/client');
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" richColors closeButton />
      <UnifiedLogin
        defaultTab={isClientPath ? 'client' : 'admin'}
        onLogin={handleLogin}
      />
    </div>
  );
}

export default App;
